import React, { useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';
import numeral from 'numeral';
import { color, Common } from '@stockbit/ui';
import {
  ComparisonContainer,
  GraphContainer,
  GraphContent,
  GraphContentInfo,
  GraphInputContainer,
  GraphSidebar,
  Input,
} from './styled';
import { ChartData, ChartOptions } from 'chart.js';

const { Flex, Text } = Common;

export const options: ChartOptions<"line"> = {
  responsive: true,
  plugins: {
    legend: {
      display: false,
    },
  },
  scales: {
    y: {
      ticks: {
        font: {
          family: 'ProximaNovaRg',
          size: 15,
        },
        callback(value) {
          return numeral(value).format('0.0 a').toUpperCase();
        },
      },
    },
    x: {
      ticks: {
        font: {
          family: 'ProximaNovaRg',
          size: 15,
        },
      },
    },
  },
};

const Note = () => (
  <div className="note">
    <Text color={color.dimgray} size="14px">
      Asumsi konservatif berdasarkan pertumbuhan IHSG sebesar 19.7% per tahun
      selama 15 tahun terakhir.
      <br /> Simulasi ini berdasarkan kinerja masa lalu dan tidak menjanjikan
      kinerja masa depan.
    </Text>
  </div>
);

const ComparisonGraph = () => {
  const depositYearlyInterest = 0.00417;
  const stockYearlyInterest = 0.011715;
  const initialMonthlyDeposit = 1000000;
  const [monthlyDeposit, setMonthlyDeposit] = useState<number>(
    initialMonthlyDeposit,
  );
  const [monthlyDepositValue, setMonthlyDepositValue] = useState<string>(
    numeral(initialMonthlyDeposit).format('0,0'),
  );
  const [savingPeriod, setSavingPeriod] = useState<number>(21);
  const [depositValueOverTime, setDepositValueOverTime] = useState([]);
  const [chartPeriods, setChartPeriods] = useState([]);
  const [stocksValueOverTime, setStocksValueOverTime] = useState([]);
  const [data, setData] = useState<ChartData<"line">>({
    labels: chartPeriods,
    datasets: [
      {
        pointRadius: 0,
        borderColor: '#0099e5',
        backgroundColor: 'rgba(255,255,255,0)',
        label: 'deposito, kenaikan 5% p.a',
        data: depositValueOverTime,
      },
      {
        pointRadius: 0,
        borderColor: '#34bf49',
        backgroundColor: 'rgba(255,255,255,0)',
        label: 'investasi saham, kenaikan 15% p.a',
        data: stocksValueOverTime,
      },
    ],
  });

  const generateSchemePeriod = () => {
    const newChartPeriods = [];
    for (let year = 0; year <= savingPeriod; year += 1) {
      newChartPeriods.push(year);
    }
    setChartPeriods(newChartPeriods);
  };

  const generateBankDepositScheme = () => {
    const newDepositValueOverTime = [];
    for (let year = 0; year <= savingPeriod; year += 1) {
      const depositValueYearly =
        (monthlyDeposit + monthlyDeposit / depositYearlyInterest) *
          (1 + depositYearlyInterest) ** (year * 12) -
        monthlyDeposit / depositYearlyInterest;
      newDepositValueOverTime.push(Math.round(depositValueYearly));
    }
    setDepositValueOverTime(newDepositValueOverTime);
  };

  const generateStockScheme = () => {
    const newStocksValueOverTime = [];
    for (let year = 0; year <= savingPeriod; year += 1) {
      const stockValueYearly =
        (monthlyDeposit + monthlyDeposit / stockYearlyInterest) *
          (1 + stockYearlyInterest) ** (year * 12) -
        monthlyDeposit / stockYearlyInterest;
      newStocksValueOverTime.push(Math.round(stockValueYearly));
    }
    setStocksValueOverTime(newStocksValueOverTime);
  };

  useEffect(generateSchemePeriod, [savingPeriod]);
  useEffect(generateStockScheme, [
    savingPeriod,
    monthlyDeposit,
    stockYearlyInterest,
  ]);
  useEffect(generateBankDepositScheme, [
    savingPeriod,
    monthlyDeposit,
    depositYearlyInterest,
  ]);

  useEffect(() => {
    const newData = {
      labels: chartPeriods,
      datasets: [
        {
          radius: 0,
          borderColor: '#0099e5',
          backgroundColor: 'rgba(255,255,255,0)',
          label: 'deposito, kenaikan 5% p.a',
          data: depositValueOverTime,
        },
        {
          radius: 0,
          borderColor: '#34bf49',
          backgroundColor: 'rgba(255,255,255,0)',
          label: 'investasi saham, kenaikan 15% p.a',
          data: stocksValueOverTime,
        },
      ],
    };

    setData(newData);
  }, [depositValueOverTime, chartPeriods, stocksValueOverTime]);

  const calculateWidth = (value) => `${String(value || 2).length * 23}px`;

  return (
    <ComparisonContainer>
      <Text size="40px" family="bold" as="h2">
        Grafik Perbandingan
      </Text>
      <GraphContainer>
        <GraphSidebar>
          <GraphInputContainer>
            <div>
              <Text size="19px" as="div">
                Investasi per Bulan
              </Text>
              <Flex align="center">
                <Text size="19px" as="div" padding="0 10px 0 0">
                  Rp
                </Text>
                <Text family="bold">
                  <Input
                    value={monthlyDepositValue}
                    onChange={(e) => {
                      e.preventDefault();
                      const value = numeral(e.target.value).value();
                      setMonthlyDeposit(value);
                      setMonthlyDepositValue(numeral(value).format('0,0'));
                    }}
                    style={{
                      width: calculateWidth(monthlyDepositValue),
                    }}
                  />
                </Text>
              </Flex>
            </div>
            <div>
              <Text size="19px" as="div">
                Jangka Waktu Investasi
              </Text>
              <Flex align="center">
                <Text family="bold">
                  <Input
                    value={savingPeriod}
                    onChange={(e) => {
                      e.preventDefault();
                      const value = numeral(e.target.value).value();
                      setSavingPeriod(value);
                    }}
                    style={{ width: calculateWidth(savingPeriod) }}
                  />
                </Text>
                <Text size="19px" as="div" padding="0 0 0 10px">
                  Tahun
                </Text>
              </Flex>
            </div>
          </GraphInputContainer>
          <Note />
        </GraphSidebar>
        <GraphContent>
          <GraphContentInfo>
            <Flex>
              <span className="sfaqchart-l l-deposito" />
              <Text size="15px" as="div">
                Kenaikan Deposito <br />
                5% per tahun
              </Text>
            </Flex>
            <Flex>
              <span className="sfaqchart-l l-saham" />
              <Text size="15px" as="div">
                Kenaikan IHSG
                <br /> 15% per tahun *)
              </Text>
            </Flex>
          </GraphContentInfo>
          <Line options={options} data={data} />
        </GraphContent>
      </GraphContainer>
      <Note />
    </ComparisonContainer>
  );
};
export default ComparisonGraph;
